import { vec3 } from "gl-matrix";
import { URLSearchParamsInit } from "react-router-dom";

import { OsrsMenuEntry } from "../components/rs/menu/OsrsMenu";
import { MenuTargetType } from "../rs/MenuEntry";
import { CacheSystem } from "../rs/cache/CacheSystem";
import { CacheLoaderFactory, getCacheLoaderFactory } from "../rs/cache/loader/CacheLoaderFactory";
import { BasTypeLoader } from "../rs/config/bastype/BasTypeLoader";
import { LocTypeLoader } from "../rs/config/loctype/LocTypeLoader";
import { NpcTypeLoader } from "../rs/config/npctype/NpcTypeLoader";
import { ObjTypeLoader } from "../rs/config/objtype/ObjTypeLoader";
import { SeqTypeLoader } from "../rs/config/seqtype/SeqTypeLoader";
import { VarManager } from "../rs/config/vartype/VarManager";
import { CustomMapSquares, addCustomMapSquares } from "../rs/map/CustomMapFileLoader";
import { MapFileIndex, getMapSquareId } from "../rs/map/MapFileIndex";
import { MapFileLoader } from "../rs/map/MapFileLoader";
import { SeqFrameLoader } from "../rs/model/seq/SeqFrameLoader";
import { Pathfinder } from "../rs/pathfinder/Pathfinder";
import { TextureLoader } from "../rs/texture/TextureLoader";
import { isTouchDevice, isWallpaperEngine } from "../util/DeviceUtil";
import { CacheList, LoadedCache } from "./Caches";
import { Camera, CameraView, ProjectionType } from "./Camera";
import { InputManager } from "./InputManager";
import { MapManager } from "./MapManager";
import { MapViewerRenderer } from "./MapViewerRenderer";
import { MapViewerRendererType, createRenderer } from "./MapViewerRenderers";
import { NpcSpawn } from "./data/npc/NpcSpawn";
import { ObjSpawn } from "./data/obj/ObjSpawn";
import { RenderDataWorkerPool } from "./worker/RenderDataWorkerPool";

const DEFAULT_RENDER_DISTANCE = isWallpaperEngine ? 512 : 128;

const CACHED_MAP_IMAGE_PREFIX = "/map-images/";

export class MapViewer {
    inputManager: InputManager = new InputManager();
    camera: Camera = new Camera(3242, -26, 3202, -245, 1862);

    pathfinder: Pathfinder = new Pathfinder();

    renderer: MapViewerRenderer;

    // Cache
    loadedCache!: LoadedCache;
    cacheSystem!: CacheSystem;
    loaderFactory!: CacheLoaderFactory;

    // The loaders below are each made the first time they're used: the game's
    // main thread needs few of them (its workers load the world), and each one
    // holds a decoded config group
    private loaders = new Map<string, unknown>();

    private getLoader<T>(name: string, create: () => T): T {
        let loader = this.loaders.get(name) as T | undefined;
        if (loader === undefined) {
            loader = create();
            this.loaders.set(name, loader);
        }
        return loader;
    }

    get textureLoader(): TextureLoader {
        return this.getLoader("texture", () => this.loaderFactory.getTextureLoader());
    }

    get seqTypeLoader(): SeqTypeLoader {
        return this.getLoader("seq", () => this.loaderFactory.getSeqTypeLoader());
    }

    get seqFrameLoader(): SeqFrameLoader {
        return this.getLoader("seqFrame", () => this.loaderFactory.getSeqFrameLoader());
    }

    get locTypeLoader(): LocTypeLoader {
        return this.getLoader("loc", () => this.loaderFactory.getLocTypeLoader());
    }

    get objTypeLoader(): ObjTypeLoader {
        return this.getLoader("obj", () => this.loaderFactory.getObjTypeLoader());
    }

    get npcTypeLoader(): NpcTypeLoader {
        return this.getLoader("npc", () => this.loaderFactory.getNpcTypeLoader());
    }

    get basTypeLoader(): BasTypeLoader {
        return this.getLoader("bas", () => this.loaderFactory.getBasTypeLoader());
    }

    get varManager(): VarManager {
        return this.getLoader("varManager", () => {
            const varManager = new VarManager(this.loaderFactory.getVarBitTypeLoader());
            const questTypeLoader = this.loaderFactory.getQuestTypeLoader();
            if (questTypeLoader) {
                varManager.setQuestsCompleted(questTypeLoader);
            }
            return varManager;
        });
    }

    mapFileLoader!: MapFileLoader;
    mapFileIndex!: MapFileIndex;

    isNewTextureAnim: boolean = false;

    // Settings

    // Tile distance
    renderDistance: number = DEFAULT_RENDER_DISTANCE;
    // Map square distance
    unloadDistance: number = 2;
    // Map square distance
    lodDistance: number = 3;

    tooltips: boolean = !isTouchDevice;
    debugId: boolean = false;

    // State
    needsSearchParamUpdate: boolean = false;
    lastTimeSearchParamsUpdated: number = 0;

    menuOpen: boolean = false;
    menuOpenedFrame: number = 0;
    menuX: number = -1;
    menuY: number = -1;
    menuEntries: OsrsMenuEntry[] = [];

    debugText?: string;

    mapImageUrls: Map<number, string> = new Map();
    minimapImageUrls: Map<number, string> = new Map();
    customMapSquareIds: Set<number> = new Set();
    customMapVersion: number = 0;
    loadingMapImageIds: Set<number> = new Set();

    cameraSpeed: number = 1;

    constructor(
        readonly workerPool: RenderDataWorkerPool,
        readonly cacheList: CacheList,
        readonly objSpawns: ObjSpawn[],
        public npcSpawns: NpcSpawn[],
        readonly mapImageCache: Cache,
        rendererType: MapViewerRendererType,
        cache: LoadedCache,
        // Lets an embedding app (the game) supply its own renderer before the
        // first map squares are queued.
        rendererFactory?: (mapViewer: MapViewer) => MapViewerRenderer,
    ) {
        this.renderer = rendererFactory
            ? rendererFactory(this)
            : createRenderer(rendererType, this);
        this.initCache(cache);
    }

    getSearchParams(): URLSearchParamsInit {
        const cx = this.camera.getPosX().toFixed(2).toString();
        const cy = -this.camera.getPosY().toFixed(2).toString();
        const cz = this.camera.getPosZ().toFixed(2).toString();

        const yaw = this.camera.yaw & 2047;

        const p = (this.camera.pitch | 0).toString();
        const y = yaw.toString();

        const params: any = {
            cx,
            cy,
            cz,
            p,
            y,
        };

        if (this.camera.projectionType === ProjectionType.ORTHO) {
            params["pt"] = "o";
            params["z"] = this.camera.orthoZoom.toString();
        }

        if (this.loadedCache.info.name !== this.cacheList.latest.name) {
            params["cache"] = this.loadedCache.info.name;
        }

        params["v"] = 1;

        return params;
    }

    applySearchParams(searchParams: URLSearchParams) {
        const cx = searchParams.get("cx");
        const cy = searchParams.get("cy");
        const cz = searchParams.get("cz");

        const pitch = searchParams.get("p");
        const yaw = searchParams.get("y");

        const v = searchParams.get("v");

        if (searchParams.get("pt") === "o") {
            this.camera.projectionType = ProjectionType.ORTHO;
        }

        const zoom = searchParams.get("z");
        if (zoom) {
            this.camera.orthoZoom = parseInt(zoom);
        }

        if (cx && cy && cz) {
            const pos = vec3.fromValues(parseFloat(cx), -parseFloat(cy), parseFloat(cz));
            this.camera.pos = pos;
        }
        if (pitch) {
            this.camera.pitch = parseInt(pitch);
            if (!v) {
                this.camera.pitch = -this.camera.pitch;
            }
        }
        if (yaw) {
            this.camera.yaw = parseInt(yaw);
            if (!v) {
                this.camera.yaw = 2048 - this.camera.yaw;
            }
        }
    }

    init(): void {
        this.workerPool.loadCachedMapImages().then((mapImageUrls) => {
            mapImageUrls.forEach((value, key) => this.mapImageUrls.set(key, value));
        });
    }

    initCache(cache: LoadedCache): void {
        this.loadedCache = cache;
        this.cacheSystem = CacheSystem.fromFiles(cache.type, cache.files);
        this.loaderFactory = getCacheLoaderFactory(cache.info, this.cacheSystem);
        this.workerPool.initCache(cache, this.objSpawns, this.npcSpawns);
        this.clearMapImageUrls();

        this.loaders.clear();

        this.mapFileLoader = this.loaderFactory.getMapFileLoader();
        this.mapFileIndex = this.mapFileLoader.mapFileIndex;

        this.isNewTextureAnim = cache.info.game === "runescape" && cache.info.revision >= 681;

        this.renderer.initCache();

        this.updateSearchParams();
    }

    setRenderer(renderer: MapViewerRenderer): void {
        this.renderer = renderer;
        this.renderer.initCache();
        this.resetMenu();
    }

    /**
     * Sets the camera position to a new arbitrary position
     * @param newView Any of the items you want to move: Position, pitch, yaw
     */
    setCamera(newView: Partial<CameraView>): void {
        if (newView.position) {
            vec3.copy(this.camera.pos, newView.position);
        }
        if (newView.pitch !== undefined) {
            this.camera.pitch = newView.pitch;
        }
        if (newView.yaw !== undefined) {
            this.camera.yaw = newView.yaw;
        }
        if (newView.fov !== undefined) {
            this.camera.fov = newView.fov;
        }
        if (newView.orthoZoom !== undefined) {
            this.camera.orthoZoom = newView.orthoZoom;
        }
        this.camera.updated = true;
    }

    updateSearchParams(): void {
        this.needsSearchParamUpdate = true;
        this.lastTimeSearchParamsUpdated = performance.now();
    }

    closeMenu = () => {
        this.menuOpen = false;
        this.menuX = -1;
        this.menuY = -1;
        this.renderer.canvas.focus();
    };

    resetMenu = () => {
        this.closeMenu();
        this.menuOpenedFrame = 0;
    };

    onExamine = (entry: OsrsMenuEntry) => {
        let lookupType: string | undefined;
        switch (entry.targetType) {
            case MenuTargetType.NPC:
                lookupType = "npc";
                break;
            case MenuTargetType.LOC:
                lookupType = "object";
                break;
            case MenuTargetType.OBJ:
                lookupType = "item";
                break;
        }
        if (lookupType) {
            window.open(
                `https://oldschool.runescape.wiki/w/Special:Lookup?type=${lookupType}&id=${entry.targetId}`,
                "_blank",
            );
        }
        this.closeMenu();
    };

    updateVars(): void {
        this.workerPool.setVars(this.varManager.values);
    }

    static getCachedMapImageUrl(mapX: number, mapY: number): string {
        return CACHED_MAP_IMAGE_PREFIX + `${mapX}_${mapY}.png`;
    }

    async queueLoadMapImage(mapX: number, mapY: number) {
        const mapManager = this.renderer.mapManager;
        const mapId = getMapSquareId(mapX, mapY);
        if (
            this.loadingMapImageIds.size > this.workerPool.size * 4 ||
            this.mapImageUrls.has(mapId) ||
            this.loadingMapImageIds.has(mapId) ||
            mapManager.invalidMapIds.has(mapId) ||
            mapManager.loadingMapIds.has(mapId)
        ) {
            return;
        }
        this.loadingMapImageIds.add(mapId);
        const customMapVersion = this.customMapVersion;

        try {
            const minimapData = await this.workerPool.queueMapImage(mapX, mapY, 0, true);
            if (customMapVersion !== this.customMapVersion) {
                return;
            }
            if (minimapData) {
                const url = URL.createObjectURL(minimapData.minimapBlob);
                // Saved images are found by square alone, so a custom square's
                // would outlive a change to it
                const custom = this.customMapSquareIds.has(mapId);
                this.setMapImageUrl(mapX, mapY, url, false, !custom);
            } else {
                mapManager.invalidMapIds.add(mapId);
            }
        } catch (e) {
            console.error("Failed to draw the map image of", mapX, mapY, e);
        } finally {
            // A failed one can be asked for again, instead of the square
            // staying "loading" (and blank) for good
            this.loadingMapImageIds.delete(mapId);
        }
    }

    // Adds map squares the cache doesn't have, or replaces them, here and in the
    // workers. Any image of one is left behind. Whatever was on its way for the
    // old ones is dropped (customMapVersion), and the renderer's own copies are the
    // caller's to clear.
    async addCustomMapSquares(squares: CustomMapSquares, removedIds: number[] = []): Promise<void> {
        this.customMapVersion++;
        addCustomMapSquares(this.mapFileLoader, squares, removedIds);
        await this.workerPool.addCustomMapSquares(squares, removedIds);
        for (const id of [...squares.keys(), ...removedIds]) {
            for (const urls of [this.mapImageUrls, this.minimapImageUrls]) {
                const url = urls.get(id);
                if (url) {
                    URL.revokeObjectURL(url);
                    urls.delete(id);
                }
            }
        }
        for (const id of removedIds) {
            this.customMapSquareIds.delete(id);
        }
        for (const id of squares.keys()) {
            this.customMapSquareIds.add(id);
            this.renderer.mapManager.invalidMapIds.delete(id);
        }
    }

    getMapImageUrl(mapX: number, mapY: number, minimap: boolean): string | undefined {
        if (mapX < 0 || mapY < 0 || mapX >= MapManager.MAX_MAP_X || mapY >= MapManager.MAX_MAP_Y) {
            return undefined;
        }
        const urls = minimap ? this.minimapImageUrls : this.mapImageUrls;
        if (minimap) {
            this.renderer.mapManager.loadMap(mapX, mapY);
        } else {
            this.queueLoadMapImage(mapX, mapY);
        }
        const mapId = getMapSquareId(mapX, mapY);
        return urls.get(mapId);
    }

    setMapImageUrl(
        mapX: number,
        mapY: number,
        url: string,
        minimap: boolean,
        cache: boolean = true,
    ): void {
        const mapId = getMapSquareId(mapX, mapY);
        const urls = minimap ? this.minimapImageUrls : this.mapImageUrls;
        const old = urls.get(mapId);
        if (old) {
            URL.revokeObjectURL(old);
        }
        if (cache) {
            fetch(url).then((resp) => {
                if (resp.ok) {
                    const request = new Request(MapViewer.getCachedMapImageUrl(mapX, mapY), {
                        headers: {
                            "RS-Cache-Name": this.loadedCache.info.name,
                        },
                    });
                    this.mapImageCache.put(request, resp);
                }
            });
        }
        urls.set(mapId, url);
    }

    clearMapImageUrls(): void {
        for (const url of this.mapImageUrls.values()) {
            URL.revokeObjectURL(url);
        }
        for (const url of this.minimapImageUrls.values()) {
            URL.revokeObjectURL(url);
        }
        this.mapImageUrls.clear();
        this.minimapImageUrls.clear();
        this.loadingMapImageIds.clear();
    }
}
